import { useState, useCallback } from "react";
import { ApiError } from "../api/client";

/**
 * Envuelve las acciones de escritura (autorizar / rechazar / reabrir /
 * reintentar) con manejo de error y refresco de la lista.
 *
 * Nace de un incidente real (10/08/2026, factura URUWARE A-790175, ver
 * docs/Decisions.md): el navegador recibió `500` a las 13:33:05, pero el
 * backend **completó la autorización con éxito** a las 13:33:06 — un segundo
 * después. Algo delante del backend (timeout de la plataforma de Static Web
 * Apps sobre el proxy same-origin) se rindió mientras la acción seguía
 * corriendo. El backend hizo lo correcto; el problema era del lado del
 * cliente:
 *
 *  1. La lista NO se refrescaba: el `await recargar()` venía DESPUÉS del
 *     `await api.autorizar(...)`, así que al fallar este último nunca se
 *     ejecutaba. La pantalla seguía mostrando la factura como pendiente
 *     cuando ya había avanzado a PENDIENTE_COMPRADOR.
 *  2. El error no se mostraba en ningún lado — quedaba como una promesa
 *     rechazada silenciosa en la consola.
 *
 * De ahí las dos reglas de acá:
 *
 * - **Siempre se refresca la lista, haya funcionado o no** (`finally`). No es
 *   un reintento de la acción — es mostrar el estado REAL del backend, que es
 *   la única fuente de verdad sobre si se aplicó. Reintentar el POST sigue
 *   estando prohibido (no son idempotentes, ver `api/client.ts`).
 * - **El error se muestra sin destruir la tabla.** El estado de error de
 *   carga reemplaza la página entera (correcto: no hay nada que mostrar); el
 *   de una acción va en un cartel arriba de la tabla, porque las otras 206
 *   filas siguen siendo válidas y usables.
 */

interface Opciones {
  /** Qué se intentaba hacer, para el mensaje ("autorizar la factura"). */
  descripcion: string;
  /**
   * Por default se refresca la lista al terminar. Se pasa `false` solo para
   * acciones que no cambian nada de lo que la tabla muestra — hoy únicamente
   * el comentario del Aprobador, que es independiente de autorizar.
   */
  recargarDespues?: boolean;
}

/** Traduce el error a algo que le sirva a quien está mirando la pantalla. */
function mensajeDeError(err: unknown, descripcion: string): string {
  if (err instanceof ApiError) {
    // 409 = el review_item no está en el estado esperado: alguien más ya lo
    // procesó. No es un fallo real, es una carrera — y el refresco que ya
    // corrió deja la lista al día.
    if (err.status === 409) {
      return `No se pudo ${descripcion}: otra persona ya la procesó. La lista se actualizó.`;
    }
    if (err.status === 403) {
      return `No tenés permiso para ${descripcion}.`;
    }
    if ([500, 502, 503, 504].includes(err.status)) {
      // Caso del incidente: el servidor pudo haber completado la acción igual.
      // Decirlo explícitamente evita que alguien reintente a ciegas y termine
      // confundido, o que asuma que no pasó nada cuando sí pasó.
      return (
        `El servidor tardó demasiado o falló al ${descripcion}. ` +
        `Ojo: la acción PUEDE haberse aplicado igual — la lista ya se actualizó, ` +
        `revisá el estado de la factura antes de reintentar.`
      );
    }
    return `No se pudo ${descripcion} (error ${err.status}).`;
  }
  // fetch que lanza = error de red. Mismo razonamiento que el 500: no se sabe
  // si la request llegó al backend o no.
  return (
    `Error de conexión al ${descripcion}. La acción PUEDE haberse aplicado igual — ` +
    `la lista ya se actualizó, revisá el estado antes de reintentar.`
  );
}

export function useAccion(recargar: () => Promise<void>) {
  const [error, setError] = useState<string | null>(null);
  const [enCurso, setEnCurso] = useState(false);

  const ejecutar = useCallback(
    // Genérica y DEVUELVE el valor de la acción (undefined si falló), para que
    // el llamador pueda usar la respuesta — el lote de Contaduría necesita leer
    // `sin_aprobador` del resultado. Asignarlo a una variable del closure en vez
    // de devolverlo no compila: TypeScript no puede seguir el flujo dentro del
    // callback y narrowea la variable a `never`.
    async <T,>(
      accion: () => Promise<T>,
      { descripcion, recargarDespues = true }: Opciones
    ): Promise<T | undefined> => {
      setError(null);
      setEnCurso(true);
      let errorDeAccion: string | null = null;
      let resultado: T | undefined;
      try {
        resultado = await accion();
      } catch (err) {
        errorDeAccion = mensajeDeError(err, descripcion);
      } finally {
        if (recargarDespues) {
          try {
            await recargar();
          } catch {
            // No pisar el error de la acción con el del refresco: el primero
            // es el que la persona necesita leer. Si el refresco falla y la
            // acción no, sí se avisa (la lista quedó vieja).
            errorDeAccion =
              errorDeAccion ??
              "La acción se completó, pero no se pudo actualizar la lista. Recargá la página.";
          }
        }
        setError(errorDeAccion);
        setEnCurso(false);
      }
      return resultado;
    },
    [recargar]
  );

  return {
    error,
    limpiarError: () => setError(null),
    /**
     * Muestra un aviso en el mismo cartel, sin que haya habido una excepción.
     *
     * Existe para los resultados PARCIALES: el lote de Contaduría puede
     * autorizar algunas y dejar otras sin autorizar (por no tener Aprobador).
     * Eso no es un error de la llamada —el POST salió bien— pero hay que
     * decírselo a quien apretó el botón. Pasarlo como excepción haría que
     * `mensajeDeError` lo reporte como "error de conexión", que sería
     * engañoso.
     */
    mostrarAviso: setError,
    enCurso,
    ejecutar,
  };
}
